import { useEffect } from "react";
import { createPortal } from "react-dom";
import axiosInstance from "../api/axiosInstance";
import { CertificateIcon, RankingIcon } from "../assets/icons";
import { useAlert } from "../context/AlertContext";
import { useAuth } from "../context/AuthContext";
import { useVerification } from "../context/VerificationContext";
import "./VerificationPopup.css";

export default function VerificationPopup() {
    const { isOpen, step, error, returnUrl, mode, targetIsVerified, currentUserVerified, closeVerification, setLoading, setSuccess, setVerified, setError } = useVerification();
    const { user, updateUser } = useAuth();
    const { showAlert } = useAlert();

    const handleVerifyClick = async () => {
        setLoading();
        try {
            const res = await axiosInstance.post("/api/stripe/create-verification-session");
            if (res.data?.url) {
                window.location.href = res.data.url;
            } else {
                throw new Error("Chybí URL pro ověření");
            }
        } catch (err) {
            console.error("Chyba při vytváření ověřovací relace:", err);
            const message = err.response?.data?.message || "Nepodařilo se spustit ověření. Zkuste to prosím znovu.";
            showAlert("error", message);
        }
    };

    // Check for success status in URL after returning from Stripe
    useEffect(() => {
        if (!isOpen) return;

        const params = new URLSearchParams(window.location.search);
        if (params.get("status") === "success") {
            setSuccess();
            window.history.replaceState({}, document.title, window.location.pathname);

            axiosInstance.get("/api/user")
                .then((res) => {
                    updateUser(res.data);
                    showAlert("success", "Váš profil byl úspěšně ověřen! Získáváte zlatý odznak.", "verify-success");
                    setTimeout(() => closeVerification(), 3000);
                })
                .catch(() => {
                    showAlert("info", "Ověření probíhá. Odznak se zobrazí po dokončení.", "verify-success");
                });
        }
    }, [isOpen, step, updateUser, showAlert, closeVerification, setSuccess]);

    // Redirect to returnUrl after success
    useEffect(() => {
        if (step === "success" && returnUrl) {
            window.location.href = returnUrl;
        }
    }, [step, returnUrl]);

    // Show error alert when error state changes
    useEffect(() => {
        if (error) {
            showAlert("error", error);
        }
    }, [error, showAlert]);

    if (!isOpen) return null;

    const isFullVerify = mode === "verify";
    const isInfoUnverified = mode === "info_unverified";
    const isInfoVerified = mode === "info_verified";

    // Button visibility: current user is unverified AND mode is verify or info_unverified
    const showVerifyBtn = (isFullVerify || isInfoUnverified) && !currentUserVerified;
    // Verification info paragraph: same as button
    const showVerificationInfo = isFullVerify || isInfoUnverified;
    // Heading: target profile is verified -> "Ověřený profil", else "Ověření profilu"
    const heading = targetIsVerified ? "Ověřený profil" : "Ověření profilu";
    // Subtitle: unverified current user clicks badge on verified target profile
    const subtitle = isInfoUnverified && targetIsVerified ? "Chcete si taky ověřit profil?" : null;
    // Button text differs for info_unverified
    const buttonText = isInfoUnverified ? "Taky ověřit profil (149Kč)" : "Ověřit profil (149Kč)";

    const popupContent = (
        <div className="popup_container" onClick={closeVerification}>
            <div className="popup" onClick={(e) => e.stopPropagation()}>

                <h2>{heading}</h2>
                {subtitle && <p className="verification_subtitle">{subtitle}</p>}

                <div className="form-review">
                    <div className="verify_subdiv">
                        <ul className="verification_benefits">
                            <li>
                                <CertificateIcon className="app-icon verify_popup certificate" stroke="1" />
                                <p className="body_base strong">Odznak "Ověřený profil"</p>
                                <p>Zvýšení důvěryhodnosti mezi kolegy i pořadateli.</p>
                            </li>
                            <li>
                                <RankingIcon className="app-icon verify_popup priority" stroke="1" />
                                <p className="body_base strong">Prioritní zájemce</p>
                                <p>Přednostní zobrazení profilu při reakci na nabídky práce a záskoků.</p>
                            </li>
                        </ul>

                        {/* Verify button for unverified users in verify/info_unverified modes */}
                        {(step === "idle" || step === "loading") && showVerifyBtn && (
                            <button
                                type="button"
                                className="form-submit extra_space half-width center "
                                onClick={handleVerifyClick}
                                disabled={step === "loading"}
                            >
                                <p className="strong">
                                    {step === "loading" ? "Přesouvám na Stripe..." : buttonText}
                                </p>
                            </button>
                        )}

                        {/* Verification info paragraph */}
                        {showVerificationInfo && (
                            <p className="body_smallest verification_info">
                                Oficiální a bezpečné ověření identity je prováděno prostřednictvím Stripe Identity vyfocením průkazu totožnosti (občanský průkaz, řidičský průkaz nebo pas) a selfie obličeje.<br></br>
                            </p>
                        )}
                    </div>

                    {/* SUCCESS STEP - Just returned from Stripe */}
                    {step === "success" && (
                        <div className="verification_success">
                            <div className="success_icon">✓</div>
                            <h3>Ověření bylo odesláno ke zpracování</h3>
                            <p>
                                Stripe Identity nyní ověřuje vaše dokumenty. Po dokončení obdržíte
                                potvrzení a váš profil bude označen zlatým odznakem "Ověřený profil".
                                Obvykle to trvá jen pár minut.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return createPortal(popupContent, document.body);
}